import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { getFragment } from '@graphql/reactQuery';
import { useQuery } from '@graphql/reactQuery';
import {
  SearchHistoryArtistDocument,
  SearchHistoryCollectorDocument,
  SearchHistoryPlaylistDocument,
  SearchHistoryReleaseDocument,
  SearchResultArtistFragmentDoc,
  SearchResultCollectorFragmentDoc,
  SearchResultReleaseFragmentDoc,
  SearchResultShelfFragmentDoc,
} from '@graphql/reactQuery';
import { ArtistRow, CollectorRow, PlaylistRow, ReleaseRow } from '../shared';
import type { SavedEntity } from './schema';
import { removeSearchHistory } from './useSearchHistoryStore';

export function SearchHistoryRow({
  entity,
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  switch (entity.type) {
    case 'Release': {
      return <SearchHistoryRelease entity={entity} onRowClick={onRowClick} />;
    }
    case 'Artist': {
      return <SearchHistoryArtist entity={entity} onRowClick={onRowClick} />;
    }
    case 'Playlist': {
      return <SearchHistoryPlaylist entity={entity} onRowClick={onRowClick} />;
    }
    case 'Collector': {
      return <SearchHistoryCollector entity={entity} onRowClick={onRowClick} />;
    }
    default: {
      const neverValue: never = entity.type;

      return null && neverValue;
    }
  }
}

/*#__PURE__*/ gql(/* GraphQL */ `
  query SearchHistoryRelease($id: UUID!) {
    release(id: $id) {
      id
      ...SearchResultRelease
    }
  }

  query SearchHistoryArtist($id: UUID!) {
    artist(id: $id) {
      id
      ...SearchResultArtist
    }
  }

  query SearchHistoryPlaylist($id: UUID!) {
    shelf: playlistV2(id: $id) {
      id
      ...SearchResultShelf
    }
  }

  query SearchHistoryCollector($id: UUID!) {
    user(id: $id) {
      id
      ...SearchResultCollector
    }
  }
`);

function SearchHistoryRelease({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryReleaseDocument, {
    variables: {
      id,
    },

    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
    staleTime: 0,
  });

  const release = getFragment(SearchResultReleaseFragmentDoc, data?.data.release);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!release) return null;

  return <ReleaseRow release={release} subtitle={type} onRowClick={onRowClick} />;
}

function SearchHistoryArtist({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryArtistDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const artist = getFragment(SearchResultArtistFragmentDoc, data?.data.artist);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!artist) return null;

  return <ArtistRow artist={artist} subtitle={type} onRowClick={onRowClick} />;
}

function SearchHistoryPlaylist({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryPlaylistDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const playlist = getFragment(SearchResultShelfFragmentDoc, data?.data.shelf);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!playlist) return null;

  return <PlaylistRow playlist={playlist} subtitle={type} onRowClick={onRowClick} />;
}

function SearchHistoryCollector({
  entity: { id, type },
  onRowClick,
}: {
  entity: SavedEntity;
  onRowClick: () => void;
}) {
  const { data } = useQuery(SearchHistoryCollectorDocument, {
    variables: {
      id,
    },
    staleTime: Infinity,
    onError(error) {
      // eslint-disable-next-line no-console
      console.error(error);

      removeSearchHistory({
        id,
      });
    },
  });

  const collector = getFragment(SearchResultCollectorFragmentDoc, data?.data.user);

  // TODO: Loading state https://linear.app/sound/issue/ENG-2044/independent-loading-state-of-search-history-rows
  if (!collector) return null;

  return <CollectorRow collector={collector} subtitle={type} onRowClick={onRowClick} />;
}
